import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Pairing,
  Bottles,
  Discover,
  type PairingSession,
  type VmpDiscoverProduct,
} from '@/lib/api';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Spinner,
  Textarea,
  Skeleton,
  Badge,
  Pill,
  DropZone,
} from '@/design-system';
import type { IconName } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { useToasts } from '@/lib/store';
import { formatPriceNok } from '@/lib/format';

type Protein = 'meat' | 'pork' | 'chicken' | 'fish' | 'vegetarian';
type Source = 'cellar' | 'catalog' | 'both';

interface ProteinDef {
  id: Protein;
  label: string;
  icon: IconName;
  /** Free-text we feed the AI to bias the dish profile. */
  hint: string;
  /** Catalog category facet + query keywords for product search. */
  catalog: { category: string; q: string };
}

const PROTEINS: ProteinDef[] = [
  {
    id: 'meat',
    label: 'Kjøtt',
    icon: 'beef',
    hint: 'rødt kjøtt',
    catalog: { category: 'Rødvin', q: 'cabernet syrah barolo bordeaux' },
  },
  {
    id: 'pork',
    label: 'Svin',
    icon: 'flame',
    hint: 'svinekjøtt',
    catalog: { category: 'Rødvin', q: 'pinot noir riesling rioja' },
  },
  {
    id: 'chicken',
    label: 'Kylling',
    icon: 'egg',
    hint: 'kylling',
    catalog: { category: 'Hvitvin', q: 'chardonnay pinot noir burgund' },
  },
  {
    id: 'fish',
    label: 'Fisk',
    icon: 'fish',
    hint: 'fisk og sjømat',
    catalog: { category: 'Hvitvin', q: 'riesling chablis sauvignon champagne' },
  },
  {
    id: 'vegetarian',
    label: 'Vegetar',
    icon: 'cherry',
    hint: 'vegetarisk',
    catalog: { category: 'Hvitvin', q: 'sauvignon blanc pinot grigio rosé' },
  },
];

const SOURCES: { id: Source; label: string; icon: IconName }[] = [
  { id: 'cellar', label: 'Min kjeller', icon: 'home' },
  { id: 'catalog', label: 'Katalogen', icon: 'globe' },
  { id: 'both', label: 'Begge', icon: 'sparkles' },
];

export function PairPage() {
  const { push } = useToasts();
  const [protein, setProtein] = useState<Protein | null>(null);
  const [dish, setDish] = useState('');
  const [source, setSource] = useState<Source>('both');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [catalogQuery, setCatalogQuery] = useState<{ q: string; category: string } | null>(null);

  const proteinDef = protein ? PROTEINS.find((p) => p.id === protein) : null;
  const canSubmit = (proteinDef != null || dish.trim().length >= 4);

  const buildPairingText = () => {
    const parts: string[] = [];
    if (proteinDef) parts.push(proteinDef.hint);
    if (dish.trim()) parts.push(dish.trim());
    return parts.join(' — ') || dish.trim();
  };

  const startCellar = useMutation({
    mutationFn: (text: string) => Pairing.recipe(text),
    onSuccess: (data) => {
      if (data.session?.id) setSessionId(data.session.id);
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const startPlate = useMutation({
    mutationFn: async (file: File) => {
      const dataUrl = await fileToBase64(file);
      const base64 = dataUrl.split(',')[1] ?? dataUrl;
      return Pairing.plate({ imageBase64: base64, mimeType: file.type });
    },
    onSuccess: (data) => {
      if (data.session?.id) setSessionId(data.session.id);
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const session = useQuery({
    queryKey: ['pairing', sessionId],
    queryFn: () => (sessionId ? Pairing.get(sessionId) : Promise.resolve(null as unknown as PairingSession)),
    enabled: Boolean(sessionId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'queued' || status === 'processing' ? 2000 : false;
    },
  });

  const catalogMatches = useQuery({
    queryKey: ['pair.catalog', catalogQuery?.q, catalogQuery?.category],
    queryFn: () =>
      Discover.search({
        q: catalogQuery!.q,
        category: catalogQuery!.category,
        sort: 'relevance',
        pageSize: 6,
      }),
    enabled: Boolean(catalogQuery),
  });

  /** "Ukens nyheter" — always shown in the side panel, regardless of search. */
  const newsQ = useQuery({
    queryKey: ['pair.nyheter'],
    queryFn: () => Discover.nyheter({ pageSize: 6 }),
    staleTime: 30 * 60_000,
  });

  const handleSubmit = () => {
    if (!canSubmit) return;
    const text = buildPairingText();
    if (source === 'cellar' || source === 'both') {
      startCellar.mutate(text);
    } else {
      setSessionId(null);
    }
    if ((source === 'catalog' || source === 'both') && proteinDef) {
      // Append free-text to bias the catalog query a bit.
      const dishWords = dish.trim().split(/\s+/).slice(0, 3).join(' ');
      const q = dishWords ? `${proteinDef.catalog.q} ${dishWords}` : proteinDef.catalog.q;
      setCatalogQuery({ q, category: proteinDef.catalog.category });
    } else {
      setCatalogQuery(null);
    }
  };

  const showCellar = source === 'cellar' || source === 'both';
  const showCatalog = source === 'catalog' || source === 'both';
  const hasSubmitted = sessionId != null || catalogQuery != null;

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(320px,1fr)_320px]">
      <section className="grid gap-6">
        <header className="grid gap-1">
          <h1 className="ui-section-title text-3xl">Mat-paring</h1>
          <p className="text-sm text-mist">
            Hva skal du spise? Vi finner viner som passer — fra kjelleren din eller katalogen.
          </p>
        </header>

        <Card className="grid gap-5 p-5">
          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-widest text-mist">Hovedingrediens</label>
            <div className="flex flex-wrap gap-2">
              {PROTEINS.map((p) => (
                <Pill
                  key={p.id}
                  active={protein === p.id}
                  icon={p.icon}
                  onClick={() => setProtein(protein === p.id ? null : p.id)}
                >
                  {p.label}
                </Pill>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <Textarea
              label="Beskriv retten (valgfritt)"
              rows={3}
              value={dish}
              placeholder="f.eks. lammelår med rosmarin og fløtepoteter, eller pasta carbonara"
              onChange={(e) => setDish(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-xs uppercase tracking-widest text-mist">Forslag fra</label>
            <div className="flex flex-wrap gap-2">
              {SOURCES.map((s) => (
                <Pill
                  key={s.id}
                  active={source === s.id}
                  icon={s.icon}
                  onClick={() => setSource(s.id)}
                >
                  {s.label}
                </Pill>
              ))}
            </div>
          </div>

          <Button
            variant="primary"
            iconLeft="sparkles"
            loading={startCellar.isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Finn vin
          </Button>

          <details className="text-xs text-mist">
            <summary className="cursor-pointer hover:text-candle">Eller, last opp et bilde av tallerkenen</summary>
            <div className="mt-3">
              <DropZone
                accept="image/*"
                onFiles={(files) => {
                  const f = files[0];
                  if (f) startPlate.mutate(f);
                }}
              />
              {startPlate.isPending && (
                <div className="mt-2 flex items-center gap-2 text-sm text-mist">
                  <Spinner /> Sender bilde…
                </div>
              )}
            </div>
          </details>
        </Card>

        {!hasSubmitted && (
          <Card className="p-8">
            <EmptyState
              icon="chef-hat"
              title="Velg ingrediens eller beskriv retten"
              description="Vi matcher mot druer, vekt og syre — og foreslår viner fra valgt kilde."
            />
          </Card>
        )}

        {showCellar && hasSubmitted && (
          <CellarSuggestions session={session.data} loading={session.isLoading || startCellar.isPending} />
        )}

        {showCatalog && hasSubmitted && (
          <CatalogSuggestions
            products={catalogMatches.data?.products ?? []}
            loading={catalogMatches.isLoading}
            heading={proteinDef ? `Katalogforslag (${proteinDef.label.toLowerCase()})` : 'Katalogforslag'}
          />
        )}
      </section>

      <aside className="grid gap-3 xl:sticky xl:top-4 xl:self-start">
        <h2 className="ui-section-title text-xl">Ukens nyheter</h2>
        <p className="text-xs text-mist">Nye slipp i katalogen denne uka.</p>
        {newsQ.isLoading && (
          <div className="grid gap-2">
            <Skeleton height={68} /> <Skeleton height={68} /> <Skeleton height={68} />
          </div>
        )}
        {newsQ.data && (
          <div className="grid gap-2">
            {newsQ.data.products.slice(0, 6).map((p) => (
              <CatalogRow key={p.code} product={p} compact />
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

function CellarSuggestions({ session, loading }: { session?: PairingSession; loading: boolean }) {
  const suggestions = (session?.suggestions ?? []).filter((s) => Boolean(s?.bottleId));
  const ids = suggestions.map((s) => s.bottleId);
  const bottlesQ = useQuery({
    queryKey: ['pair.bottles', ids.join(',')],
    queryFn: async () => {
      if (ids.length === 0) return [];
      const fetched = await Promise.all(ids.map((id) => Bottles.get(id).catch(() => null)));
      return fetched.filter(Boolean) as NonNullable<Awaited<ReturnType<typeof Bottles.get>>>[];
    },
    enabled: ids.length > 0,
  });
  const byId = new Map((bottlesQ.data ?? []).map((b) => [b.id, b]));

  if (loading) {
    return (
      <Card className="grid gap-3 p-5">
        <div className="flex items-center gap-2 text-sm text-candle"><Spinner /> Tenker over kjelleren…</div>
        <Skeleton height={60} /><Skeleton height={60} />
      </Card>
    );
  }

  if (session?.status === 'failed') {
    return <Card className="p-5"><EmptyState icon="warning" title="Noe gikk galt" /></Card>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid gap-3">
      <h2 className="ui-section-title text-2xl">Fra kjelleren din</h2>
      {suggestions.length === 0 ? (
        <Card className="p-5">
          <EmptyState
            icon="bottle-empty"
            title="Ingen perfekte treff i kjelleren"
            description="Sjekk katalogforslag under, eller utvid søket."
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {suggestions.map((s, idx) => {
            const b = byId.get(s.bottleId);
            return (
              <Link
                key={`${s.bottleId}-${idx}`}
                to={`/bottle/${s.bottleId}`}
                className="ui-card ui-card--interactive grid grid-cols-[auto_1fr_auto] items-center gap-4 p-4"
              >
                <div className="relative grid h-16 w-12 place-items-center overflow-hidden rounded bg-ink-soft">
                  <BottleImage src={b?.photoUrl} iconSize={20} />
                </div>
                <div className="grid">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-lg text-parchment line-clamp-1">{b?.name ?? s.bottleId}</span>
                    <Badge tone="gold">#{idx + 1}</Badge>
                  </div>
                  {b && (
                    <span className="text-xs text-mist">
                      {[b.region, b.country, b.vintage].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {s.reasoning && <span className="mt-1 text-sm text-parchment-dim line-clamp-2">{s.reasoning}</span>}
                </div>
                <div className="grid gap-1 text-right">
                  <span className="font-serif text-2xl text-candle">
                    {Number.isFinite(s.score) ? (s.score * 100).toFixed(0) : '-'}
                  </span>
                  <span className="text-[9px] uppercase tracking-widest text-mist">match</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function CatalogSuggestions({
  products,
  loading,
  heading,
}: {
  products: VmpDiscoverProduct[];
  loading: boolean;
  heading: string;
}) {
  if (loading) {
    return (
      <Card className="grid gap-3 p-5">
        <div className="flex items-center gap-2 text-sm text-candle"><Spinner /> Søker i katalogen…</div>
        <Skeleton height={60} /><Skeleton height={60} />
      </Card>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid gap-3">
      <h2 className="ui-section-title text-2xl">{heading}</h2>
      {products.length === 0 ? (
        <Card className="p-5">
          <EmptyState icon="search" title="Fant ingen treff" description="Prøv en annen rett eller en annen kilde." />
        </Card>
      ) : (
        <div className="grid gap-3">
          {products.map((p) => <CatalogRow key={p.code} product={p} />)}
        </div>
      )}
    </motion.div>
  );
}

function CatalogRow({ product, compact }: { product: VmpDiscoverProduct; compact?: boolean }) {
  const img = useMemo(
    () =>
      product.images?.find((i) => i.format === 'product')?.url ??
      product.images?.[0]?.url,
    [product.images],
  );
  const price = product.price?.value;
  const region = product.district?.name ?? product.main_country?.name;
  const href = `/bottle/${encodeURIComponent(product.code)}`;
  return (
    <Link
      to={href}
      className="ui-card ui-card--interactive grid grid-cols-[auto_1fr_auto] items-center gap-3 p-3"
    >
      <div className={`relative grid place-items-center overflow-hidden rounded bg-ink-soft ${compact ? 'h-12 w-9' : 'h-16 w-12'}`}>
        <BottleImage src={img} iconSize={compact ? 16 : 20} />
      </div>
      <div className="grid min-w-0">
        <span className={`font-serif text-parchment line-clamp-1 ${compact ? 'text-sm' : 'text-base'}`}>
          {product.name}
        </span>
        <span className="text-[11px] text-mist line-clamp-1">
          {[product.main_sub_category?.name, region].filter(Boolean).join(' · ') || '—'}
        </span>
      </div>
      <div className="grid gap-0.5 text-right">
        {price != null && <span className="font-serif text-candle">{formatPriceNok(price)}</span>}
        {product.alcohol?.formattedValue && (
          <span className="text-[10px] text-mist">{product.alcohol.formattedValue}</span>
        )}
      </div>
    </Link>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
